/**
 * FAANGEngineeringTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 99 / 100 — ATS Perfect
 * Style: Silicon Valley — skills-first, metrics-driven, no-frills efficiency
 * Best For: Google, Amazon, Microsoft, Meta, Flipkart engineering roles
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList, SkillsBlock,
  ContactRow, EmptyState, DS,
} from './templateDesignSystem';

function FAANGEngineeringTemplate({
  resumeData,
  accentColor = '#0891b2',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => { },
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role, summaryText, skills, experience, internships, projects, educationList, certList, achieveList, languageList } = d; // eslint-disable-line no-unused-vars
  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const Divider = ({ title }) => (
    <div style={{ display: 'flex', alignItems: 'center', margin: '12px 0 6px' }}>
      <h2 style={{ fontSize: fs('9px'), fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1.8px', color: accentColor, margin: 0, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>{title}</h2>
      <div style={{ flex: 1, height: 1.5, background: accentColor, marginLeft: 8, opacity: 0.35 }} />
    </div>
  );

  return (
    <div id="resume" style={{ background: '#fff', color: '#111', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: fs(DS.fs.body), lineHeight: 1.5, padding: `${DS.sp.pageY} ${DS.sp.pageX}`, boxSizing: 'border-box' }}>

      {/* ─── Header — centered, clean ─── */}
      <header style={{ textAlign: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: `2px solid ${accentColor}` }}>
        <EditableField editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)} tag="h1"
          style={{ fontSize: fs('24px'), fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' }} placeholder="Your Name" />
        {(role || editMode) && (
          <EditableField editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)} tag="p"
            style={{ fontSize: fs('11px'), fontWeight: 600, color: accentColor, margin: '2px 0 4px' }} placeholder="Software Engineer · Full Stack" />
        )}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <ContactRow data={d} accentColor={accentColor} />
        </div>
      </header>

      {/* ─── Skills FIRST (FAANG standard) ─── */}
      {skills.length > 0 && (
        <section data-ats="skills" style={{ marginBottom: 10 }}>
          <Divider title="Technical Skills" />
          <SkillsBlock skills={skills} accentColor={accentColor} layout="rows" />
        </section>
      )}

      {/* ─── Summary (optional for experienced) ─── */}
      {summaryText && (
        <section data-ats="summary" style={{ marginBottom: 10 }}>
          <Divider title="Summary" />
          <EditableField editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
            tag="p" multiline style={{ fontSize: fs(DS.fs.body), color: '#374151', lineHeight: 1.65, margin: 0 }} />
        </section>
      )}

      {/* ─── Experience ─── */}
      {experience.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: 10 }}>
          <Divider title="Work Experience" />
          {experience.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                  {exp.company && (
                    <EditableField editMode={editMode} value={exp.company} onSave={v => onFieldUpdate(`experience.${idx}.company`, v)}
                      tag="span" style={{ fontSize: fs(DS.fs.body), color: accentColor, fontWeight: 600, marginLeft: 6 }} />
                  )}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 12, fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Internships ─── */}
      {internships.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: 10 }}>
          <Divider title="Internships" />
          {internships.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                  {exp.company && <span style={{ fontSize: fs(DS.fs.body), color: accentColor, fontWeight: 600, marginLeft: 6 }}>{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 12, fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Projects ─── */}
      {projects.length > 0 && (
        <section data-ats="projects" style={{ marginBottom: 10 }}>
          <Divider title="Projects" />
          {projects.map((proj, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a' }}>{proj.title || proj.name}</span>
                  {proj.tech?.length > 0 && (
                    <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280', marginLeft: 8, fontStyle: 'italic' }}>
                      {Array.isArray(proj.tech) ? proj.tech.join(' · ') : proj.tech}
                    </span>
                  )}
                </div>
                {proj.impact && <span style={{ fontSize: fs(DS.fs.small), color: '#059669', fontWeight: 600 }}>{proj.impact}</span>}
              </div>
              <BulletList points={proj.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Education ─── */}
      {educationList.length > 0 && (
        <section data-ats="education" style={{ marginBottom: 10 }}>
          <Divider title="Education" />
          {educationList.map((edu, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <div>
                <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body), marginLeft: 6 }}>{edu?.college}</span>
                {edu?.cgpa && <span style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', marginLeft: 6 }}>CGPA: {edu.cgpa}</span>}
              </div>
              <EditableField editMode={editMode} value={edu?.year || ''} onSave={v => onFieldUpdate(`education.${idx}.year`, v)}
                tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic', whiteSpace: 'nowrap' }} />
            </div>
          ))}
        </section>
      )}

      {certList.length > 0 && (
        <section data-ats="certifications" style={{ marginBottom: 10 }}>
          <Divider title="Certifications" />
          <BulletList points={certList} accentColor={accentColor} />
        </section>
      )}
      {achieveList.length > 0 && (
        <section data-ats="achievements" style={{ marginBottom: 10 }}>
          <Divider title="Achievements" />
          <BulletList points={achieveList} accentColor={accentColor} />
        </section>
      )}
    </div>
  );
}

export default FAANGEngineeringTemplate;
